import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { toast, ToastContainer } from 'react-toastify';
import { Table } from 'semantic-ui-react';

import Button from '../../components/Button';
import Heading from '../../components/Heading';
import FromToDatePicker from '../../components/FromToDatepicker';
import Loader from '../../components/Loader';
import Pagination from '../../components/Pagination';
import CountDisplay from '../../components/CountDisplay';
import VerifyModal from './components/VerifyModal';
import Dropdown from '../../components/Dropdown/Dropdown';

import user from '../../store/user';
import { ACTIVE_RECRUITERS_UNVERIFIED_COMP, RECRUITER_PROFILE_MGT_EDIT } from '../../models/routes';
import { getQueryParams } from '../../utils/utilFunctions';

import { getUnmappedRecruiters } from '../../services/getUnmappedRecruiters';
import { getUnmappedRecruitersCount } from '../../services/getUnmappedRecruitersCount';
import verifyCompanyManagement from '../../services/verifyCompanyManagement';

import './styles.scss';
import { adminFeatureMapping } from '../../models/adminFeatureMapping';

/**
 * Dashboard which has the following functions:
 * 1. show all recruiters who have posted atleast one job within the selected given time frame and are not associated
 *    with any verified companies
 * 2. user can verify a company from this dashboard.
 * 3. the verify modal will have the option to change the unverified company 
 * 4. verify modal will show the list of recruiters associated with the selected unverified company
 * 5. verify modal will confirm before taking any action
 */
const ActiveRecruitersUnverifiedCompanies = props => {
    const paymentFilters = [
        { name: "Paid", id: 1 },
        { name: "Unpaid", id: 0 },
    ];

    const [adminId, setAdminId] = useState(null);
    const [isPaid, setIsPaid] = useState(1);

    const [recruiters, setRecruiters] = useState([]);
    const [total, setTotal] = useState(0);
    const [isInitial, setIsInitial] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(0);
    const [modalData, setModalData] = useState({});

    const resetModalData = () => {
        setModalData({
            id: null,
            name: null,
            email: null,
            unverifiedCompanyId: null,
            domains: null,
            isVerifyLoading: false,
        });
    }

    const pageContent = 50;

    const [dateRange, setDateRange] = useState({
        startDate: moment().subtract(7, 'days').startOf('day').toDate(),
        endDate: moment().startOf('day').toDate(),
        error: null,
    });

    const tableHeading = (
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell width="one">Recruiter ID</Table.HeaderCell>
                <Table.HeaderCell width="one">Name</Table.HeaderCell>
                <Table.HeaderCell width="one">Email</Table.HeaderCell>
                <Table.HeaderCell width="one">Unverified Company id</Table.HeaderCell>
                <Table.HeaderCell width="one" className="actions">Actions</Table.HeaderCell>
            </Table.Row>
        </Table.Header>
    );

    // get list of recruiters with the following conditions:
    // 1. company_id in recruiter_profile is null
    // 2. company_id in recruiter_verification_status == 0 and unverified_company_id in recruiter_verification_status != 0
    // 3. has posted atleast one job in the selected date range
    const getRecruiterList = async (pg = null, start = null, end = null, paid = isPaid) => {
        try {

            console.log("Caalleed");
            let from = dateRange.startDate;
            let to = dateRange.endDate;
            
            if (start) from = start;
            if (end) to = end;

            const params = {
                pageNumber: pg || pageNumber,
                pageContent: 50,
                startDate: `${moment(from).format('YYYY-MM-DD')} 00:00:00`,
                endDate: `${moment(to).format('YYYY-MM-DD')} 00:00:00`,
                isPaid: paid
            };

            const response = await getUnmappedRecruiters(adminId, params);

            if (response && response.status == 200) {
                if (response.data && response.data.data) {
                    setRecruiters(response.data.data);
                }
            }
        } catch (error) {
            console.log(error);
        }

        setIsLoading(false);
        setIsPageLoading(false);
    };

    // get count of recruiters for pagination
    const getRecruiterCount = async (start = null, end = null, paid = isPaid) => {
        try {

            let from = dateRange.startDate;
            let to = dateRange.endDate;

            if (start) from = start;
            if (end) to = end;

            const params = {
                startDate: `${moment(from).format('YYYY-MM-DD')} 00:00:00`,
                endDate: `${moment(to).format('YYYY-MM-DD')} 00:00:00`,
                isPaid: paid
            };

            const response = await getUnmappedRecruitersCount(adminId, params);

            if (response && response.status == 200) {
                setTotal(response.data.data.total);

                // If no results and isPaid not set in URL, try with isPaid=0
                if (isInitial && paid == 1 && response.data.data.total == 0) {
                    paid = 0 ;
                    setIsPaid(paid);
                    setIsInitial(false);
                    // console.log("HHHHH --");
                    // await getRecruiterList(pg, start, end, paid);
                    // await getRecruiterCount(start, end, paid);
                }

            }
        } catch (error) {
            // console.log(error);
        }
    };

    // when user clicks on verify company, open modal with recruiter data
    const openVerifyCompanyModal = recruiterObj => {
        setModalData({
            id: recruiterObj.id,
            name: recruiterObj.name,
            email: recruiterObj.email,
            unverifiedCompanyId: recruiterObj.unverified_company_id,
            domains: recruiterObj.domains,
        });
    }

    // when user clicks search button
    const handleOnSearch = () => {
        setIsLoading(true);
        getRecruiterList();
        getRecruiterCount();
        updateQueryParams();
    };

    // when page changes 
    const handlePageChange = page => {
        setIsPageLoading(true);
        setPageNumber(page);
        getRecruiterList(page);
        updateQueryParams(page);
    };

    useEffect(() => {

        if (adminId) {
            setIsLoading(true);
            getRecruiterList(pageNumber, dateRange.startDate, dateRange.endDate, isPaid);
            getRecruiterCount(dateRange.startDate, dateRange.endDate, isPaid);
            updateQueryParams(pageNumber, isPaid);
        }
    }, [isPaid]);

    // when verify company is clicked, call endpoint
    const handleOnVerifyCompany = async (unverifiedCompanyId, companyData) => {
        setModalData(prevObj => ({ ...prevObj, isVerifyLoading: true }));

        try {
            await verifyCompanyManagement(adminId, unverifiedCompanyId, {
                ...companyData,
                moderated: 1, // set moderated = 1, since this is a company moderation dashboard
            });

            toast.success('Company has been verified');

            resetModalData(); // close modal and clear modal data
        } catch (error) {
            // console.log(error);
            toast.error('Error verifying company. Please contact tech team');
            setModalData(prevObj => ({ ...prevObj, isVerifyLoading: false }));
        }
    }

    // update query params with current control values
    const updateQueryParams = (page,paid = isPaid) => {
        const queryArr = [];

        let pg = pageNumber;

        if (page) {
            pg = page;
        }

        queryArr.push(`pg=${pg}`);
        queryArr.push(`start=${moment(dateRange.startDate).format('YYYY-MM-DD')}`);
        queryArr.push(`end=${moment(dateRange.endDate).format('YYYY-MM-DD')}`);
        queryArr.push(`isPaid=${paid}`);

        props.history.push(`${ACTIVE_RECRUITERS_UNVERIFIED_COMP}?${queryArr.join('&')}`);
    }

    // when profile is fetched, start fetching data for the dashboard
    useEffect(() => {
        if (adminId) {
            const queryParams = getQueryParams(window.location.search);

            const pg = queryParams.pg || 1;
            const start = queryParams.start ? moment(queryParams.start).toDate() : dateRange.startDate;
            const end = queryParams.end ? moment(queryParams.end).toDate() : dateRange.endDate;
            let paid = queryParams.isPaid !== undefined ? parseInt(queryParams.isPaid) : 1;

            setDateRange({
                startDate: start,
                endDate: end
            });

            setPageNumber(pg);
            setIsPaid(paid);
            
            getRecruiterList(pg, start, end, paid);
            getRecruiterCount(start, end, paid);
            updateQueryParams(pg,paid);
        }
    }, [adminId]);

    // fetch admin details on page load
    useEffect(() => {
        async function getProfile() {
            const profile = await user.getProfile();

            return profile;
        }

        getProfile().then(data => {
            if (!user.checkFeatureAccess(adminFeatureMapping.COMPANY_MODERATION)) {
                window.location.href='/notAdmin'
            }

            setAdminId(data.id);
        })
    }, []);

    // whenever date range changes, check if date range is valid
    useEffect(() => {
        if (dateRange && dateRange.startDate && dateRange.endDate) {
            const start = moment(dateRange.startDate);
            const end = moment(dateRange.endDate);

            if (end.diff(start, 'days') > 31) {
                setDateRange(prevObj => {
                    return {
                        ...prevObj,
                        error: 'Date range cannot be more than one month'
                    }
                });
            } else {
                setDateRange(prevObj => {
                    return {
                        ...prevObj,
                        error: null
                    }
                });
            }
        }
    }, [dateRange.startDate, dateRange.endDate])

    let mainContent;

    if (isLoading) {
        mainContent = (
            <Loader />
        );
    } else if (isPageLoading) {
        mainContent = (
            <>
                <Table>
                    {tableHeading}
                </Table>

                <Loader />
            </>
        )
    } else if (!isLoading && (!recruiters || recruiters.length === 0)) {
        mainContent = (
            <div>No records found!</div>
        );
    } else {
        mainContent = (
            <Table className="recruiter__table">
                {tableHeading}
                <Table.Body>
                    {
                        recruiters.map((item, index) => {
                            return (
                                <Table.Row className="recruiter__table--row">
                                    <Table.Cell verticalAlign="top">{item.id}</Table.Cell>
                                    <Table.Cell verticalAlign="top">{item.name}</Table.Cell>
                                    <Table.Cell verticalAlign="top">{item.email}</Table.Cell>
                                    <Table.Cell verticalAlign="top">{item.unverified_company_id}</Table.Cell>
                                    <Table.Cell verticalAlign="top">
                                        <div className='actions'>
                                            <div 
                                                className='btn-actions btn-verify'
                                                onClick={() => {
                                                    openVerifyCompanyModal(item)
                                                }}
                                            >
                                                Verify Company and Map Company ID
                                            </div>

                                            <div className='btn-actions btn-edit-profile'>
                                                <a target='__blank' href={`${RECRUITER_PROFILE_MGT_EDIT}?recid=${item.id}`}>Edit Profile</a>
                                            </div>

                                            {item.unverified_company_id ? (
                                                <div className='btn-actions btn-edit-company'>
                                                    <a target='__blank' href={`managementCompany/${item.unverified_company_id}?status=2`}>Edit Company</a>
                                                </div>
                                            ) : null}
                                        </div>
                                    </Table.Cell>
                                </Table.Row>
                            )
                        })
                    }
                </Table.Body>
            </Table>
        )
    };

    let topPageIndicators;
    let bottomPageIndicators;

    if (!isLoading && recruiters && recruiters.length > 0) {
        topPageIndicators = (
            <div className='row text-center top-page-controller'>
                <Pagination totalItemsCount={total} content={pageContent} pageNumber={pageNumber} handlePageChange={handlePageChange} />
                <CountDisplay start={(pageNumber - 1) * pageContent + 1} end={pageNumber * pageContent} total={total} />
            </div>
        );

        if (!isPageLoading) {
            bottomPageIndicators = (
                <div className='row text-center bottom-page-controller'>
                    <Pagination totalItemsCount={total} content={pageContent} pageNumber={pageNumber} handlePageChange={handlePageChange} />
                </div>
            );
        }
    } else {
        topPageIndicators = <div></div>;
        bottomPageIndicators = <div></div>;
    }

    return (
        <div className='page-content active-rec-unverified-comp'>
            <ToastContainer 
                position='bottom-left'
                hideProgressBar
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnVisibilityChange
                draggable={false}
                pauseOnHover
            />

            <div className='page-heading'>
                <Heading text="Unmapped Active Recruiters" />
            </div>

            <div className='controller__container'>
                <div className='controller__container--left'>
                    <div className='control'>
                        <div className='label'>
                            Date: 
                        </div>
                        <FromToDatePicker
                            name="date"
                            startDate={dateRange.startDate}
                            endDate={dateRange.endDate}
                            handleChangeStart={value => {
                                setDateRange({
                                    ...dateRange,
                                    startDate: moment(value).toDate(),
                                });
                            }}
                            handleChangeEnd={value => {
                                setDateRange({
                                    ...dateRange,
                                    endDate: moment(value).toDate(),
                                });
                            }}
                        />
                    </div>
                    <div className='actions'>
                        <Button 
                            className="btn-search" 
                            text="Search" 
                            skin={'dark'} 
                            clickButton={handleOnSearch} 
                            disabled={dateRange.error ? true : null}
                        />
                    </div>
                </div>
                <div className='controller__container--right'>
                    <Dropdown
                        data={paymentFilters}
                        dropdownChange={(e) => {
                            const newValue = parseInt(e.target.value);
                            setIsPaid(newValue);
                        }}
                        defaultValue={isPaid}
                    />
                </div>
                {dateRange.error ? <div className='error'>Date range cannot be more than 1 month</div> : null}
            </div>

            {topPageIndicators}

            <div className='main-content'>
                {mainContent}
            </div>

            {bottomPageIndicators}

            {modalData && modalData.id ? (
                <VerifyModal 
                    adminId={adminId}
                    recruiterData={{
                        id: modalData.id,
                        name: modalData.name,
                        email: modalData.email,
                        unverifiedCompanyId: modalData.unverifiedCompanyId,
                    }}
                    isVerifyLoading={modalData.isVerifyLoading}
                    onVerifyCompany={handleOnVerifyCompany}
                    onClose={() => {
                        resetModalData();
                    }}
                />
            ) : null}
        </div>
    );
};

export default ActiveRecruitersUnverifiedCompanies;